import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { signup } from "@/lib/api";
import { ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { ContourBackground } from "@/components/marketing/SresVisuals";
import useSEO from "@/lib/useSEO";

/**
 * Mobile-first SRES signup page — matches the /login layout.
 * Single column, brand block on top, request-access card below.
 */
export default function SignupPage() {
  useSEO({
    title: "Request access",
    description:
      "Request an SRES workspace account. All signups are gated by the super-administrator.",
    path: "/signup",
  });

  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", name: "", organisation: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setPending(true);
    try {
      await signup(f);
      toast.success("Signup received — awaiting super-admin approval");
      nav("/login?pending=1", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-white flex flex-col overflow-x-hidden"
      data-testid="signup-page"
    >
      <MarketingHeader />

      <main
        className="flex-1 relative w-full px-5 sm:px-6 py-10 sm:py-14 overflow-hidden"
        style={{
          background:
            "radial-gradient(900px 600px at 50% -10%, rgba(30,111,255,0.08), transparent 60%), #FFFFFF",
        }}
      >
        <div
          className="absolute inset-0 grid-lines opacity-40 pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-20 w-[720px] max-w-none h-[560px] opacity-[0.55] pointer-events-none"
          aria-hidden
        >
          <ContourBackground />
        </div>

        <div className="relative w-full max-w-[440px] mx-auto flex flex-col gap-6">
          {/* Brand block */}
          <div className="text-center pt-2" data-testid="signup-brand-block">
            <div className="flex justify-center">
              <img
                src="/sres-logo-horizontal.png?v=5"
                alt="SRES · Shanvi Resource Estimation Studio"
                className="h-12 sm:h-14 w-auto object-contain max-w-full"
                draggable={false}
              />
            </div>
            <div className="mt-5 inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
              <span className="w-6 h-px bg-brand-blue" />
              Request access
            </div>
            <h1
              className="mt-4 font-heading font-black text-slate-900 text-3xl sm:text-4xl tracking-[-0.02em]"
              data-testid="signup-heading"
            >
              Create an account
            </h1>
            <p className="mt-3 text-slate-500 text-[14px] sm:text-[15px] leading-relaxed">
              All signups are gated by the super-administrator. You&apos;ll
              receive access once approved.
            </p>
          </div>

          {/* Card */}
          <form
            onSubmit={submit}
            className="bg-white border border-slate-200 rounded-2xl shadow-[0_20px_60px_-30px_rgba(15,23,42,0.18)] p-5 sm:p-8"
            data-testid="signup-form"
          >
            <div className="space-y-4">
              <Field
                label="Full name"
                value={f.name}
                onChange={(v) => set("name", v)}
                testId="signup-name"
                autoComplete="name"
              />
              <Field
                label="Organisation"
                value={f.organisation}
                onChange={(v) => set("organisation", v)}
                testId="signup-org"
                autoComplete="organization"
              />
              <Field
                label="Work email"
                type="email"
                inputMode="email"
                value={f.email}
                onChange={(v) => set("email", v)}
                required
                testId="signup-email"
                autoComplete="email"
              />
              <div>
                <Label className="text-[13px] font-semibold text-slate-800">
                  Password (min 8 chars)
                </Label>
                <div className="relative mt-2">
                  <Input
                    type={showPassword ? "text" : "password"}
                    required
                    value={f.password}
                    onChange={(e) => set("password", e.target.value)}
                    className="h-12 bg-slate-50 border-slate-200 rounded-md text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand-blue focus-visible:border-brand-blue focus-visible:bg-white text-[16px] pr-11"
                    autoComplete="new-password"
                    data-testid="signup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-md"
                    data-testid="signup-toggle-password"
                  >
                    {showPassword ? (
                      <EyeSlash size={18} weight="regular" />
                    ) : (
                      <Eye size={18} weight="regular" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={pending}
                className="w-full h-12 bg-slate-900 hover:bg-black text-white font-semibold rounded-md text-[15px] mt-2 inline-flex items-center justify-center gap-2"
                data-testid="signup-submit"
              >
                {pending ? (
                  "Submitting…"
                ) : (
                  <>
                    Request access <ArrowRight size={16} weight="bold" />
                  </>
                )}
              </Button>
            </div>

            <div className="mt-7 pt-6 border-t border-slate-100 text-center text-[13.5px] text-slate-500">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-brand-blue font-semibold hover:underline"
                data-testid="link-login"
              >
                Sign in
              </Link>
            </div>
          </form>

          <div className="text-center text-[11.5px] text-slate-400 tracking-wide">
            Approvals are typically completed within 1 business day.
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  testId,
  type = "text",
  inputMode,
  required = false,
  autoComplete,
}) {
  return (
    <div>
      <Label className="text-[13px] font-semibold text-slate-800">{label}</Label>
      <Input
        type={type}
        inputMode={inputMode}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoCapitalize={type === "email" ? "none" : undefined}
        autoCorrect={type === "email" ? "off" : undefined}
        spellCheck={type === "email" ? false : undefined}
        className="mt-2 h-12 bg-slate-50 border-slate-200 rounded-md text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand-blue focus-visible:border-brand-blue focus-visible:bg-white text-[16px]"
        data-testid={testId}
      />
    </div>
  );
}
